/**
 * Mythium Preview Renderer
 *
 * Renders an acrylic die-cut mini next to a true-size top-down base view.
 * Three modes:
 *   - hasAlpha + full preview: SVG with feMorphology die-cut pipeline + base
 *   - !hasAlpha: rectangular image fallback + base
 *   - compact mode: simple thumbnail (used in Your Set list)
 *
 * Mini-hoogte en base-diameter staan op dezelfde mm→px schaal, dus de
 * onderlinge verhouding klopt vanzelf per maat.
 */

(function () {
  'use strict';

  // 1mm ≈ 3.78px at standard 96dpi (CSS reference pixel).
  const MM_TO_PX = 3.78;

  // True-size base disc diameters: 1 inch bases for S/M, 2 inch base for L.
  const BASE_SIZES_MM = {
    small:  25.4,
    medium: 25.4,
    large:  50.8,
  };

  // Mini height in mm per size — drives both the rendered px height
  // and (via MM_TO_PX) the proportion vs the base disc.
  const MINI_HEIGHTS_MM = {
    small:  35,
    medium: 50,
    large:  70,
  };

  // Dialable: cutline edge thickness in mm (acrylic rim around the figure).
  const CUTLINE_MM = 2.1;

  // Cutline colour for the dark-on-light acryl look. Dialable.
  const CUTLINE_COLOR = '#1a1208';

  const BLUR_PAD = 14;

  let uidCounter = 0;
  const nextUid = () => `mp${Date.now().toString(36)}${(++uidCounter).toString(36)}`;

  function computeImageDimensions(size, aspectRatio) {
    const mm = MINI_HEIGHTS_MM[size] || MINI_HEIGHTS_MM.medium;
    const imgH = Math.round(mm * MM_TO_PX);
    const imgW = Math.round(imgH * aspectRatio);
    return { width: imgW, height: imgH };
  }

  /**
   * True-size base disc + dual-unit label next to the Choose-size buttons.
   * The circle is drawn at its real diameter via CSS mm units (a 96dpi
   * approximation, which is acceptable here); the stylesheet caps it with
   * max-width so it scales down instead of overflowing on small screens.
   * Colour/shape come from the .mythium-base-indicator__circle CSS class.
   */
  function renderBaseIndicator(size) {
    const mm = BASE_SIZES_MM[size] || BASE_SIZES_MM.medium;
    const inch = Math.round(mm / 25.4); // 25.4mm → 1", 50.8mm → 2"
    return `
<span class="mythium-base-indicator__circle" style="width:${mm}mm" aria-hidden="true"></span>
<span class="mythium-base-indicator__label">Ø ${mm} mm / ${inch} inch</span>`;
  }

  function renderPreview(opts) {
    const o = opts || {};
    const imageUrl = o.imageUrl;
    // Optional pre-computed mask whose alpha drives the cutline filter.
    // When provided, the cutline traces the mask's silhouette instead of
    // the image's own alpha — interior holes in the image get no cutline.
    const cutlineMaskUrl = (typeof o.cutlineMaskUrl === 'string' && o.cutlineMaskUrl) ? o.cutlineMaskUrl : null;
    // Optional pre-coloured rim band. When provided, the directional
    // acryl rim replaces the dark cutline-filter band entirely.
    const rimColorUrl = (typeof o.rimColorUrl === 'string' && o.rimColorUrl) ? o.rimColorUrl : null;
    // Rim PNG is built on a padded canvas (so the band can extend below
    // figure-touches-bottom-edge cases). padFraction is the source-pixel
    // pad divided by source width — we scale it into SVG coords via imgW.
    const rimPadFraction = typeof o.rimPadFraction === 'number' ? o.rimPadFraction : 0;
    const size = o.size || 'medium';
    const aspectRatio = o.aspectRatio || 0.75;
    const hasAlpha = !!o.hasAlpha;
    const alphaCenterX = typeof o.alphaCenterX === 'number' ? o.alphaCenterX : 0.5;
    const alphaBottomFraction = typeof o.alphaBottomFraction === 'number' ? o.alphaBottomFraction : 1.0;
    const compact = !!o.compact;

    if (!imageUrl) return '';

    if (compact) {
      return `<div class="mythium-preview-compact" style="position:relative;width:100%;height:100%">
        <img src="${imageUrl}" alt="Mini" style="width:100%;height:100%;object-fit:contain"/>
      </div>`;
    }

    const dims = computeImageDimensions(size, aspectRatio);
    const imgW = dims.width;
    const imgH = dims.height;
    const borderPx = Math.round(CUTLINE_MM * MM_TO_PX);

    if (!hasAlpha) {
      // Rectangular fallback — no die-cut
      return `
<div class="mythium-preview__layout" style="user-select:none">
  <div class="mythium-preview__mini">
    <img src="${imageUrl}" alt="Mini preview" style="max-width:100%;max-height:50rem;height:auto;display:block"/>
  </div>
</div>`;
    }

    // Die-cut with feMorphology pipeline (split: cutline edges + sheen).
    const uid = nextUid();
    const cutlineId = `acf-cut-${uid}`;
    const sheenId = `acf-sheen-${uid}`;

    const cx = Math.max(0, Math.min(1, alphaCenterX));
    const leftImgPx = cx * imgW;
    const rightImgPx = (1 - cx) * imgW;
    const halfSvgW = Math.max(leftImgPx, rightImgPx) + borderPx;
    const baseSvgW = Math.ceil(halfSvgW * 2);
    const baseImgOffsetX = Math.round(halfSvgW - leftImgPx);

    const solidFraction = Math.max(0.1, Math.min(1.0, alphaBottomFraction));
    const solidImgH = Math.round(imgH * solidFraction);

    // Rim-padding in SVG pixels. Shift figure inward and grow the
    // viewport so the wider rim band (which extends below/above/around
    // the figure) has room to draw without being clipped.
    const rd = Math.round(rimPadFraction * imgW);
    const svgW = baseSvgW + 2 * rd;
    const svgH = solidImgH + borderPx + 2 * rd;
    const imgOffsetX = baseImgOffsetX + rd;
    const imgOffsetY = borderPx + rd;

    return `
<div class="mythium-preview__layout" style="user-select:none">
  <div class="mythium-preview__mini">
    <svg viewBox="0 0 ${svgW} ${svgH}" style="display:block;max-width:100%;max-height:50rem;width:100%;height:auto;overflow:visible" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="${cutlineId}" x="-${BLUR_PAD}" y="-${BLUR_PAD}" width="${svgW + 2 * BLUR_PAD}" height="${svgH + 2 * BLUR_PAD}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feMorphology in="SourceAlpha" operator="dilate" radius="${borderPx}" result="dilated"/>
          <feGaussianBlur in="dilated" stdDeviation="4" result="blurred"/>
          <feColorMatrix in="blurred" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 50 -22" result="smoothed"/>
          <feMorphology in="smoothed" operator="dilate" radius="2" result="innerDilate"/>
          <feComposite in="innerDilate" in2="smoothed" operator="out" result="edgeBand"/>
          <feFlood flood-color="${CUTLINE_COLOR}" result="edgeColor"/>
          <feComposite in="edgeColor" in2="edgeBand" operator="in" result="edge"/>
          <feMerge>
            <feMergeNode in="edge"/>
          </feMerge>
        </filter>
        <filter id="${sheenId}" x="-${BLUR_PAD}" y="-${BLUR_PAD}" width="${svgW + 2 * BLUR_PAD}" height="${svgH + 2 * BLUR_PAD}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feMorphology in="SourceAlpha" operator="erode" radius="10" result="deepInner"/>
          <feGaussianBlur in="deepInner" stdDeviation="6" result="sheenMask"/>
          <feFlood flood-color="rgba(255,255,255,0.04)" result="sheenColor"/>
          <feComposite in="sheenColor" in2="sheenMask" operator="in" result="materialSheen"/>
          <feMerge>
            <feMergeNode in="materialSheen"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      ${rimColorUrl
        ? `<image href="${rimColorUrl}" x="${imgOffsetX - rd}" y="${imgOffsetY - rd}" width="${imgW + 2 * rd}" height="${solidImgH + 2 * rd}" preserveAspectRatio="none"/>`
        : `<g filter="url(#${cutlineId})">
        <image href="${cutlineMaskUrl || imageUrl}" x="${imgOffsetX}" y="${imgOffsetY}" width="${imgW}" height="${solidImgH}" preserveAspectRatio="xMidYMin slice"/>
      </g>`}
      <image href="${imageUrl}" x="${imgOffsetX}" y="${imgOffsetY}" width="${imgW}" height="${solidImgH}" filter="url(#${sheenId})" preserveAspectRatio="xMidYMin slice"/>
    </svg>
  </div>
</div>`;
  }

  window.MythiumPreviewRenderer = {
    renderPreview: renderPreview,
    renderBaseIndicator: renderBaseIndicator,
  };
})();
